import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const LOAD_TRANSACTION_REQUEST = 'app/AdminPickupReviewPage/LOAD_TRANSACTION_REQUEST';
export const LOAD_TRANSACTION_SUCCESS = 'app/AdminPickupReviewPage/LOAD_TRANSACTION_SUCCESS';
export const LOAD_TRANSACTION_ERROR = 'app/AdminPickupReviewPage/LOAD_TRANSACTION_ERROR';

export const RESOLVE_REQUEST = 'app/AdminPickupReviewPage/RESOLVE_REQUEST';
export const RESOLVE_SUCCESS = 'app/AdminPickupReviewPage/RESOLVE_SUCCESS';
export const RESOLVE_ERROR = 'app/AdminPickupReviewPage/RESOLVE_ERROR';

export const CLEAR_RESOLVED = 'app/AdminPickupReviewPage/CLEAR_RESOLVED';

// ================ Reducer ================ //

const initialState = {
  transaction: null,
  // Denormalized related entities keyed by `${type}:${uuid}`.
  included: {},
  // Fresh presigned view URL per S3 photo key (minted server-side).
  photoUrls: {},
  failedPhotoKeys: [],
  loading: false,
  error: null,
  resolving: false,
  resolved: false,
  resolveError: null,
};

export default function adminPickupReviewPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case LOAD_TRANSACTION_REQUEST:
      return { ...state, loading: true, error: null };
    case LOAD_TRANSACTION_SUCCESS:
      return {
        ...state,
        loading: false,
        transaction: payload.transaction,
        included: payload.included,
        photoUrls: payload.photoUrls,
        failedPhotoKeys: payload.failedPhotoKeys,
      };
    case LOAD_TRANSACTION_ERROR:
      return { ...state, loading: false, error: payload };

    case RESOLVE_REQUEST:
      return { ...state, resolving: true, resolveError: null, resolved: false };
    case RESOLVE_SUCCESS:
      return { ...state, resolving: false, resolved: true };
    case RESOLVE_ERROR:
      return { ...state, resolving: false, resolveError: payload };

    case CLEAR_RESOLVED:
      return { ...state, resolved: false, resolveError: null };

    default:
      return state;
  }
}

export const clearResolved = () => ({ type: CLEAR_RESOLVED });

// ================ Thunks ================ //

// Same admin read path as the dispute page: the operator is not a party
// to the booking, so the participant-scope SDK 403s; the endpoint reads
// via the Integration API and mints fresh presigned photo URLs. It also
// collects pickupIssuePhotos (added for this page).
export const loadTransaction = transactionId => dispatch => {
  dispatch({ type: LOAD_TRANSACTION_REQUEST });
  return fetch(`/api/admin/dispute/show?transactionId=${encodeURIComponent(transactionId)}`, {
    method: 'GET',
    credentials: 'include',
  })
    .then(async res => {
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(body?.error || `Failed to load review (${res.status})`);
        err.status = res.status;
        throw err;
      }
      const included = (body?.data?.included || []).reduce((acc, entity) => {
        acc[`${entity.type}:${entity.id?.uuid}`] = entity;
        return acc;
      }, {});
      dispatch({
        type: LOAD_TRANSACTION_SUCCESS,
        payload: {
          transaction: body?.data?.data,
          included,
          photoUrls: body?.photoUrls || {},
          failedPhotoKeys: body?.failedKeys || [],
        },
      });
      return body;
    })
    .catch(e => {
      dispatch({ type: LOAD_TRANSACTION_ERROR, payload: storableError(e) });
      throw e;
    });
};

/**
 * Resolve a pickup-refund review.
 *
 * @param {string} transactionId
 * @param {object} resolution
 * @param {string} resolution.outcome  'approve' | 'deny' | 'resume'
 * @param {string} resolution.notes
 * @param {string} resolution.requestedFrom  'accepted' | 'kit-ready' —
 *   picks the resume target (from protectedData.pickupRefundRequestedFrom)
 */
export const resolvePickupReview = (transactionId, resolution) => dispatch => {
  dispatch({ type: RESOLVE_REQUEST });

  const { outcome, notes, requestedFrom } = resolution;
  const transition =
    outcome === 'approve'
      ? 'transition/operator-approve-pickup-refund'
      : outcome === 'deny'
      ? 'transition/operator-deny-pickup-refund'
      : requestedFrom === 'accepted'
      ? 'transition/operator-resume-pickup-accepted'
      : 'transition/operator-resume-pickup-kit-ready';

  const body = {
    transactionId: new UUID(transactionId),
    transition,
    params: {
      protectedData: {
        pickupRefundResolution: { notes: notes || '' },
      },
    },
  };

  return fetch('/api/operator-transition', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
    .then(async response => {
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        let message = `HTTP ${response.status}`;
        try {
          message = JSON.parse(text)?.error || message;
        } catch (e) {
          // non-JSON error body — keep the status message
        }
        throw new Error(message);
      }
      dispatch({ type: RESOLVE_SUCCESS });
      return response;
    })
    .catch(e => {
      dispatch({ type: RESOLVE_ERROR, payload: storableError(e) });
      throw e;
    });
};
